/**
 * One section of a surface: the legacy steps it absorbed, stacked.
 *
 * Phase 8 moves the chrome, not the step components. Each step renders inside
 * its own panel under the section's heading, so a merged section reads as one
 * screen while the steps behind it are still the ones the tests know.
 *
 * @module router/programme-editor/SectionRoute
 */

import React, { Suspense, useCallback, useEffect, useState } from "react";

import { useLocation } from "react-router";

import { EditLockProvider, Heading, WarningAlert } from "@/components/ui";
import { FormPanelBones } from "@/components/ui/SkeletonParts";
import { useProgramme } from "@/hooks/useStore";
import { useWorkspaceOutletContext } from "@/router/workspace-context";
import { lockPrerequisites } from "@/state/step-locks";

import { editorAvailability, LEASE_HELD_ELSEWHERE } from "./editor-availability";
import { LockedNotice } from "./LockedNotice";
import { SectionNav } from "./SectionNav";
import { useEditorBase } from "./SurfaceLayout";
import {
  applicableStateKeys,
  isStateKeyUnlocked,
  type SectionDef,
  sectionLockedKey,
  sectionLockedReason,
  stateKeyLockedReason,
  STEP_COMPONENTS,
  stepTitle,
} from "./surfaces";

export interface SectionRouteProps {
  section: SectionDef;
}

/** Reports a lazy step as committed, which is only once its `Suspense` resolves. */
function StepMounted({
  stateKey,
  onMount,
}: {
  stateKey: string;
  onMount: (stateKey: string) => void;
}): null {
  useEffect(() => {
    onMount(stateKey);
  }, [stateKey, onMount]);
  return null;
}

export function SectionRoute({ section }: SectionRouteProps): React.ReactElement {
  const {
    shouldRenderReadOnlyEditorView,
    canEditWorkflowState,
    canEditProgramme,
    viewingProgramme,
    editorSync,
  } = useWorkspaceOutletContext();
  // The shell leaves "locked" out of its read-only rule because the lease
  // covers the structure alone; this is where that half is applied.
  const leaseHeldElsewhere = section.structural && editorSync.status === "locked";
  const readOnly = editorAvailability({
    readOnly: shouldRenderReadOnlyEditorView,
    workflowState: editorSync.record?.workflowState,
    canEditWorkflowState,
    canEditProgramme,
    viewing: viewingProgramme,
  });
  const lock = leaseHeldElsewhere ? LEASE_HELD_ELSEWHERE : readOnly;
  // Subscribed: the lock below reads the store, and unlocks as it changes.
  useProgramme();
  const base = useEditorBase();
  const lockedKey = sectionLockedKey(section);
  const lockedReason = sectionLockedReason(section);
  const stateKeys = lockedReason ? [] : applicableStateKeys(section.stateKeys);

  // A tab for a section's second step carries that step's anchor as a hash,
  // and a navigation inside the app never scrolls to one. Held until every
  // open step has mounted: the steps above the anchor load lazily, and
  // scrolling before they do lands on a target they later push down.
  const { hash } = useLocation();
  const [mountedKeys, setMountedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const markMounted = useCallback((stateKey: string) => {
    setMountedKeys((current) => (current.has(stateKey) ? current : new Set(current).add(stateKey)));
  }, []);
  const stepsMounted = stateKeys
    .filter((stateKey) => isStateKeyUnlocked(stateKey))
    .every((stateKey) => mountedKeys.has(stateKey));
  useEffect(() => {
    if (hash && stepsMounted) {
      document.getElementById(hash.slice(1))?.scrollIntoView({ block: "start" });
    }
  }, [hash, stepsMounted]);

  return (
    // `id="content"` is the anchor a finding scrolls to and two specs locate.
    // `tabIndex={-1}` keeps it out of the tab order but lets the findings
    // drawer focus it after sending the reader here.
    <div
      id="content"
      tabIndex={-1}
      className="space-y-4"
      data-testid={`section-${section.segment}`}
    >
      <Heading level={2} className="sr-only">
        {section.title}
      </Heading>
      {/* Before the content it affects, not after it. The workflow and role
          reasons are the header's to give; this one covers the section alone. */}
      {leaseHeldElsewhere ? <WarningAlert>{LEASE_HELD_ELSEWHERE.full}</WarningAlert> : null}
      <EditLockProvider
        decision={readOnly}
        lease={leaseHeldElsewhere ? LEASE_HELD_ELSEWHERE : null}
      >
        <div
          className="space-y-4"
          data-testid="programme-editor-content-shell"
          data-edit-locked={lock !== null || undefined}
        >
          {/* The tab strip disables a locked section, but a typed or bookmarked
            address reaches it anyway, so the content is gated here too. */}
          {lockedReason ? (
            <LockedNotice
              base={base}
              prerequisites={lockedKey ? lockPrerequisites(lockedKey) : []}
              reason={lockedReason}
              readOnly={lock}
              reasonShown={leaseHeldElsewhere}
            />
          ) : null}
          {/* A section either names its own component or the legacy step keys it
            absorbed; the keys are what carry the `step-<key>` anchors. */}
          {section.component && !lockedReason ? (
            <Suspense fallback={<FormPanelBones label={`Loading ${section.title}…`} />}>
              <section.component />
            </Suspense>
          ) : null}
          {stateKeys.map((stateKey) => {
            const Step = STEP_COMPONENTS[stateKey];
            // One step of an open section can still be shut: electives wait on
            // setup while the modules beside them are already editable. The
            // anchor stays, so a finding naming the step still lands on why.
            return (
              <section key={stateKey} id={`step-${stateKey}`} aria-label={stepTitle(stateKey)}>
                {isStateKeyUnlocked(stateKey) ? (
                  <Suspense fallback={<FormPanelBones label={`Loading ${stepTitle(stateKey)}…`} />}>
                    <Step />
                    <StepMounted stateKey={stateKey} onMount={markMounted} />
                  </Suspense>
                ) : (
                  <LockedNotice
                    base={base}
                    subject={stepTitle(stateKey)}
                    prerequisites={lockPrerequisites(stateKey)}
                    reason={stateKeyLockedReason(stateKey)}
                    readOnly={lock}
                    reasonShown={leaseHeldElsewhere}
                  />
                )}
              </section>
            );
          })}
        </div>
      </EditLockProvider>
      <SectionNav />
    </div>
  );
}
